import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

interface ConversationJobsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jobs: any[];
  isLoading?: boolean;
}

const ConversationJobs: React.FC<ConversationJobsProps> = ({
  jobs,
  isLoading,
}) => {
  if (isLoading || !jobs || !Array.isArray(jobs)) {
    return null;
  }

  // Filter for in-progress jobs (status starts with 'JQ' = queued, 'JR' = running)
  const inProgressJobs = jobs.filter(job => {
    const status = job.status || '';
    return status.startsWith('JQ') || status.startsWith('JR');
  });

  if (inProgressJobs.length === 0) {
    return null;
  }

  const getStatusIcon = (status: string) => {
    const statusCode = status?.split('#')[0] || status;
    switch (statusCode) {
      case 'JQ':
        return <ClockIcon className="text-chariot-text-secondary h-4 w-4" />;
      case 'JR':
        return (
          <div className="bg-chariot-brand-primary h-4 w-4 animate-pulse rounded-full" />
        );
      case 'JP':
        return <CheckCircleIcon className="text-success h-4 w-4" />;
      case 'JF':
        return <XCircleIcon className="text-error h-4 w-4" />;
      default:
        return <ClockIcon className="text-chariot-text-secondary h-4 w-4" />;
    }
  };

  const getStatusText = (status: string) => {
    const statusCode = status?.split('#')[0] || status;
    switch (statusCode) {
      case 'JQ':
        return 'Queued';
      case 'JR':
        return 'Running';
      case 'JP':
        return 'Complete';
      case 'JF':
        return 'Failed';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="from-chariot-background-secondary to-chariot-background-primary border-chariot-stroke mb-4 rounded-lg border bg-linear-to-r p-4 shadow-xs">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex items-center gap-2">
          <ClockIcon className="text-chariot-brand-primary h-4 w-4" />
          <span className="text-chariot-text-primary text-sm font-semibold">
            Active Security Scans
          </span>
        </div>
        <span className="bg-chariot-brand-primary inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-medium text-white">
          {inProgressJobs.length}
        </span>
      </div>

      <div className="space-y-2">
        {inProgressJobs.map((job, index) => (
          <div
            key={job.key || index}
            className="bg-chariot-background-primary border-chariot-stroke/50 flex items-center justify-between rounded-md border p-2"
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex-shrink-0">{getStatusIcon(job.status)}</div>
              <div className="min-w-0 flex-1">
                <div className="text-chariot-text-primary truncate text-sm font-medium">
                  {job.source || job.capability || 'Security Scan'}
                </div>
                {job.target && (
                  <div className="text-chariot-text-secondary truncate text-xs">
                    Target:{' '}
                    {typeof job.target === 'object'
                      ? job.target.model?.source || JSON.stringify(job.target)
                      : job.target}
                  </div>
                )}
              </div>
            </div>
            <div className="ml-3 flex-shrink-0">
              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  job.status?.startsWith('JR')
                    ? 'bg-chariot-brand-primary/10 text-chariot-brand-primary border-chariot-brand-primary/20 border'
                    : 'bg-chariot-background-secondary text-chariot-text-secondary border-chariot-stroke border'
                }`}
              >
                {getStatusText(job.status)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="border-chariot-stroke/50 mt-3 border-t pt-3">
        <div className="text-chariot-text-secondary flex items-center gap-2 text-xs">
          <div className="bg-chariot-brand-primary h-2 w-2 animate-pulse rounded-full"></div>
          <span>
            Results will be automatically summarized when scans complete
          </span>
        </div>
      </div>
    </div>
  );
};

export default ConversationJobs;
